import { Text, WasabiButton } from '@kk/ui';
import { useState } from 'react';
import { NavLink, Outlet } from 'react-router';

import { kaLogoUrl } from '@/assets';
import { useClients, useMembership, useSession, useSuperAdmin } from '@/auth/SessionProvider';
import { CountPill } from '@/components/bits';
import { Sheet } from '@/components/Sheet';
import { deck } from '@/copy/deck';
import { portalLanguage } from '@/copy/activeLanguage';
import { CLUB_GUIDE_URL } from '@/lib/guides';
import { ADMIN_URL } from '@/lib/host';
import { placeholders } from '@/copy/placeholders';
import { useApprovals, useGuidesPage, useOrg, useRedemptions } from '@/data/hooks';
import { initials } from '@/lib/age';
import type { OrgSummary } from '@/types/wire';

import { BillingBanner } from './BillingBanner';
import { useIsPhone } from './useIsPhone';

interface NavEntry {
  label: string;
  to: string;
  count?: { n: number; tone: 'teal' | 'magenta' };
  /** Set apart above by a rule: the one row that leaves this club's portal for Kiddie Kredit's own admin. */
  separated?: boolean;
  /** An absolute URL to another site, drawn as an anchor — react-router would read it as a path. */
  external?: boolean;
  /** Opens in a new tab: a document to read beside the portal, not a place to go instead of it. */
  newTab?: boolean;
  testID?: string;
}

function useNav(): NavEntry[] {
  const approvals = useApprovals();
  const redemptions = useRedemptions();
  const membership = useMembership();
  const orgAdmin = membership?.role === 'orgAdmin';
  const superAdmin = useSuperAdmin();
  const guides = useGuidesPage();
  return [
    {
      label: deck.nav.approvals,
      to: '/approvals',
      count: { n: approvals.data?.length ?? 0, tone: 'teal' },
    },
    { label: deck.nav.roster, to: '/roster' },
    /*
     * **INVITE FAMILIES, IN THE MENU** (DECISIONS 93.3, Evan 23 Oct: a new club's first real job). It was reachable only
     * by a small link on Roster, and not at all from the phone menu, while the billing banner says "Set up billing to
     * invite families". The label is the screen's own title. Org admins only, like Groups: the screen lists the club's
     * groups to choose from, which only an org admin may read (the API answers FORBIDDEN otherwise).
     */
    ...(orgAdmin ? [{ label: deck.o09.title, to: '/invite-families', testID: 'nav-invite-families' }] : []),
    {
      label: deck.nav.redemptions,
      to: '/redemptions',
      count: { n: redemptions.data?.waiting.length ?? 0, tone: 'magenta' },
    },
    { label: deck.nav.assignments, to: '/assignments' },
    // O05 is org-admin only (the API answers FORBIDDEN otherwise): the item is absent for a group admin.
    ...(orgAdmin ? [{ label: deck.nav.groupsCodes, to: '/groups' }] : []),
    // O08 shipped 2026-09-21: the tab appears now that every field on it has a write behind it
    ...(orgAdmin ? [{ label: deck.nav.settings, to: '/settings' }] : []),
    /*
     * **THE DOOR INTO KIDDIE KREDIT'S OWN ADMIN, and the only item here that is not a club's** (Evan,
     * 2026-09-22, choosing it over a footer link and over landing staff on the rail).
     *
     * It exists because the requirement was checked against production and failed: a super admin who is also
     * staff of a club — which is every super admin today — signs in, lands on `/approvals` in the club shell,
     * and the dark rail is reachable only by typing `/admin/quest`. *"The super-admin sidebar with Quest Jr.
     * content must be visible without anyone knowing a URL."* A rail you can only see once you are already on
     * it is not a way in.
     *
     * **`separated` rather than a heading**, because the board's own note is that the dark rail means *"you are
     * in Kiddie Kredit's own admin, not an org's portal"* — the two chromes are deliberately different, so the
     * one row that crosses between them is set apart rather than filed under the club's own screens.
     *
     * Hidden for everyone else, and hidden by the same read the routes gate on (`useSuperAdmin`), never by
     * catching a 403: a refusal tells you a request was refused; a read tells you what state you are in.
     */
    /*
     * **STRAIGHT AT THE ADMIN ADDRESS** (Evan, 2026-09-23) — this is the one way a super admin reaches the
     * content admin, so it goes there directly rather than through `clubs/admin/quest`, which exists only to
     * keep old bookmarks working and costs a second page load to do it.
     */
    ...(superAdmin ? [{ label: deck.o06.nav, to: `${ADMIN_URL}/quest`, external: true, separated: true, testID: 'nav-quest-admin' }] : []),
    // DECISIONS 81 and 84: the Club Guide, in the portal's language, once kiddiekredit.com/guides is live
    ...(guides
      ? [{ label: deck.nav.clubGuide, to: CLUB_GUIDE_URL[portalLanguage()], external: true, separated: !superAdmin, newTab: true, testID: 'nav-club-guide' }]
      : []),
  ];
}

function NavItems({ onNavigate }: { onNavigate?: () => void }) {
  const items = useNav();
  return (
    <nav className="nav" aria-label={deck.nav.approvals}>
      {items.map((item) =>
        /*
         * An external item is an ANCHOR, not a NavLink: react-router would read an absolute URL as a path and
         * navigate to `/https://admin.kiddiekredit.com/quest` inside this SPA. It also has no active state —
         * a row pointing at another site is never the current one.
         */
        item.external ? (
          <a
            key={item.label}
            href={item.to}
            className={`nav-item${item.separated ? ' nav-item-apart' : ''}`}
            onClick={onNavigate}
            data-testid={item.testID}
            {...(item.newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
          >
            <Text variant="chip" tone="slate">
              {item.label}
            </Text>
          </a>
        ) : (
        <NavLink
          key={item.label}
          to={item.to}
          className={({ isActive }) =>
            `nav-item${isActive ? ' active' : ''}${item.separated ? ' nav-item-apart' : ''}`
          }
          onClick={onNavigate}
          data-testid={item.testID}
        >
          {({ isActive }) => (
            <>
              <Text variant={isActive ? 'chipSelected' : 'chip'} tone={isActive ? 'link' : 'slate'}>
                {item.label}
              </Text>
              {item.count ? <CountPill count={item.count.n} tone={item.count.tone} /> : null}
            </>
          )}
        </NavLink>
        ),
      )}
    </nav>
  );
}

/**
 * "Homework Club · Reading Club": every group of this person's, by name (the 23 Oct audit, T1-2). The phone header and
 * the role line used to name the first group alone, which read as "you are in Homework Club" to someone who staffs two.
 */
function groupNames(org: OrgSummary | undefined): string | undefined {
  const names = (org?.groups ?? []).map((g) => g.name);
  return names.length ? deck.roster.joinNames(names) : undefined;
}

/** O05 footer "Signed in as Org admin"; O01–O04 "Group admin · Homework Club". */
function RoleLine({ role, org }: { role: 'orgAdmin' | 'groupAdmin'; org: OrgSummary | undefined }) {
  if (role === 'orgAdmin')
    return (
      <Text variant="time" tone="muted" numberOfLines={1} testID="role-line">
        {deck.o05.signedInAs}{' '}
        <Text variant="labelStrong" tone="ink">
          {deck.shell.orgAdmin}
        </Text>
      </Text>
    );
  return (
    <Text variant="time" tone="muted" numberOfLines={1} testID="role-line">
      {deck.shell.groupAdmin(groupNames(org))}
    </Text>
  );
}

function OrgPicker() {
  const { session, orgId, setOrgId } = useSession();
  const memberships = session?.memberships ?? [];
  if (memberships.length < 2) return null;
  return (
    <select
      className="org-select"
      aria-label={placeholders.signIn.orgPicker}
      value={orgId ?? ''}
      onChange={(e) => setOrgId(e.target.value)}
    >
      {memberships.map((m) => (
        <option key={m.orgId} value={m.orgId}>
          {m.orgName}
        </option>
      ))}
    </select>
  );
}

function StaffFooter({ org }: { org: OrgSummary | undefined }) {
  const { session } = useSession();
  const membership = useMembership();
  const { auth } = useClients();
  /*
   * **THE SIGN-OUT USED TO VANISH WITH THE MEMBERSHIP, and that is why it could not be found** (Evan,
   * 2026-09-23). This footer returned null for `!membership`, taking the only sign-out on the club portal with
   * it — so precisely the person most likely to want one, somebody signed in to an account that belongs to no
   * club, had no way out except clearing site data.
   *
   * Now only the identity ROW needs a membership (it draws a role in a club); the sign-out is bound to being
   * signed in, which is the thing it actually ends.
   */
  if (!session) return null;
  const name = membership?.displayName ?? session.user.name ?? session.user.email;
  return (
    <div className="sidebar-footer">
      <div className="staff-row">
        <div className="initials">
          <Text variant="labelStrong" tone="ink">
            {initials(session.user.name, session.user.email)}
          </Text>
        </div>
        <div className="cell grow">
          <Text variant="rowTitleSm" tone="ink" numberOfLines={1}>
            {name}
          </Text>
          {membership ? <RoleLine role={membership.role} org={org} /> : null}
        </div>
      </div>
      <WasabiButton
        label={placeholders.signIn.signOut}
        variant="text"
        textSize="sm"
        fullWidth={false}
        onPress={() => void auth.signOut()}
        testID="sign-out"
      />
      <div className="powered-by" data-testid="powered-by">
        <Text variant="badge" tone="muted">
          {deck.shell.poweredBy}
        </Text>
        <img src={kaLogoUrl} alt={deck.shell.kreditAcademy} />
      </div>
    </div>
  );
}

/**
 * The club's mark, top left: its INITIALS in the disc, because a tag is what fits (Evan, 57(b)). It used to be the
 * Kiddie Kredit app icon standing in for a logo no club has — a generic glyph where "TEST" or "BGC" belongs — the same
 * wrong-field bug as O03's preview badge, one layer out. `initials` comes from the org summary, which the API derives
 * when a club has none; until that read lands the disc is empty rather than wrong.
 */
function OrgMark({ org }: { org: OrgSummary | undefined }) {
  return (
    <div className="org-mark" data-testid="org-mark">
      {org?.initials ? (
        <Text variant="badge" tone="white" numberOfLines={1}>
          {org.initials}
        </Text>
      ) : null}
    </div>
  );
}

function Sidebar({ org }: { org: OrgSummary | undefined }) {
  const membership = useMembership();
  const branch = org?.branches[0]?.name;
  return (
    <aside className="sidebar">
      <OrgPicker />
      <div className="lockup">
        <OrgMark org={org} />
        <div className="lockup-text">
          <Text variant="bodySmBold" tone="ink" numberOfLines={2}>
            {membership?.orgName ?? ''}
          </Text>
          {branch ? (
            <Text variant="time" tone="muted" numberOfLines={1}>
              {branch}
            </Text>
          ) : null}
        </div>
      </div>
      <NavItems />
      <StaffFooter org={org} />
    </aside>
  );
}

function PhoneHeader({ org }: { org: OrgSummary | undefined }) {
  const membership = useMembership();
  const [menu, setMenu] = useState(false);
  const approvals = useApprovals();
  const redemptions = useRedemptions();
  return (
    <>
      <header className="phone-header">
        <OrgMark org={org} />
        <div className="cell grow">
          <Text variant="bodySmBold" tone="ink" numberOfLines={1}>
            {membership?.orgName ?? ''}
          </Text>
          <Text variant="time" tone="muted" numberOfLines={1}>
            {groupNames(org) ?? ''}
          </Text>
        </div>
        <button
          type="button"
          className="menu-button"
          aria-label={placeholders.sheet.menu}
          onClick={() => setMenu(true)}
        >
          <Text variant="cardTitle" tone="slate">
            ≡
          </Text>
        </button>
      </header>
      <div className="segments">
        <NavLink
          to="/approvals"
          className={({ isActive }) => `segment${isActive ? ' active' : ''}`}
        >
          {({ isActive }) => (
            <Text variant="chipSelected" tone={isActive ? 'white' : 'ink'}>
              {deck.phone.approvalsSegment(approvals.data?.length ?? 0)}
            </Text>
          )}
        </NavLink>
        <NavLink
          to="/redemptions"
          className={({ isActive }) => `segment${isActive ? ' active' : ''}`}
        >
          {({ isActive }) => (
            <Text variant="chipSelected" tone={isActive ? 'white' : 'ink'}>
              {deck.phone.redemptionsSegment(redemptions.data?.waiting.length ?? 0)}
            </Text>
          )}
        </NavLink>
      </div>
      {menu ? (
        <Sheet
          title={
            <Text variant="cardTitle" tone="ink">
              {membership?.orgName ?? ''}
            </Text>
          }
          onClose={() => setMenu(false)}
        >
          <OrgPicker />
          <NavItems onNavigate={() => setMenu(false)} />
          <StaffFooter org={org} />
        </Sheet>
      ) : null}
    </>
  );
}

/** Desktop: 224px sidebar + main. Phone (at and under portalLayout.phoneMax, O07): header + segment pills + the screen. */
export function Shell() {
  const isPhone = useIsPhone();
  // UNAUTHORIZED on this (or any) query signs the user out from SessionProvider; nothing to watch here.
  const org = useOrg();
  if (isPhone)
    return (
      <div>
        <PhoneHeader org={org.data} />
        <main className="phone-main">
          <BillingBanner />
          <Outlet context={{ org: org.data, isPhone }} />
        </main>
      </div>
    );
  return (
    <div className="app">
      <Sidebar org={org.data} />
      <main className="main">
        <BillingBanner />
        <Outlet context={{ org: org.data, isPhone }} />
      </main>
    </div>
  );
}

export interface ShellContext {
  org: OrgSummary | undefined;
  isPhone: boolean;
}
